import { useState } from 'react';
import { Segmented } from 'antd';
import { BarChartOutlined, InboxOutlined } from '@ant-design/icons';

import { DataTable, Tile, TileHeader } from '../../../../components/shared';
import { Badge, Empty } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { KHOI_BANG, LE_BANG } from '../../chung/bang';
import { TRONG, soNguyen, tyLe } from '../../chung/nghiep-vu/so-thong-ke';
import { soTien } from '../../chung/nghiep-vu/so-tien';
import {
  NHOM_PHAN_RA,
  NHOM_PHAN_RA_NEN_TANG,
  type NhomAi,
  type NhomPhanRa,
  type ThongKeAi,
} from '../../chung/nghiep-vu/types-thong-ke';
import { nhanTenant, useNguonVet } from '../nguon';
import { cotFox, cotTokenThat } from './cot-fox';
import { LienKetKhaiGia } from './NhanTinCay';

/**
 * Phân rã theo mô hình / service / tính năng / đơn vị / người dùng (mục 8.2) — port `thong-ke/BangPhanRa.tsx` native.
 *
 * MỘT bảng đổi nhóm, không phải năm bảng xếp dọc: cùng bộ cột ở cả năm nhóm nên đổi nhóm là đổi dữ liệu, không đổi
 * cách đọc.
 *
 * Hai ô trống không được thành số 0: `do_tre_tb_ms === null` là KHÔNG LƯỢT NÀO khai độ trễ; `so_luot === 0` thì tỷ lệ
 * lỗi là `—` — "chưa có lượt nào" khác "chưa lượt nào hỏng".
 *
 * Khác native: thanh chọn nhóm nằm hàng riêng dưới tiêu đề và cuộn ngang được — năm nhãn cạnh tiêu đề tràn ở 768px.
 * Cổng tenant: chỉ token Fox (ADR-0123 mục 5). Cổng nền tảng có thêm nhóm theo tenant (mở sẵn), token thật, và cột giá
 * vốn khi API trả.
 */
export function BangPhanRa({
  thongKe,
  nhanKhoa,
}: {
  thongKe: ThongKeAi;
  /** Đổi id/mã ở ba nhóm đơn vị, người dùng, tính năng ra tên — xem `loc-ai.ts::nhanKhoaPhanRa`. */
  nhanKhoa?: (nhom: NhomPhanRa, khoa: string) => string;
}) {
  const { t, locale } = useT();
  const { cotTenant, tenTenant, nenTang } = useNguonVet();
  const cacNhom = cotTenant ? NHOM_PHAN_RA_NEN_TANG : NHOM_PHAN_RA;
  const [nhomChon, setNhom] = useState<NhomPhanRa>(cacNhom[0]);
  // Đang xem nhóm "Tenant" rồi lọc một tenant thì nhóm ấy rời thanh chọn: về nhóm đầu, không giữ một giá trị không còn nút.
  const nhom: NhomPhanRa = (cacNhom as readonly NhomPhanRa[]).includes(nhomChon) ? nhomChon : cacNhom[0];
  const dong = thongKe[nhom] ?? [];
  const coGiaVon = dong.some((r) => r.chi_phi_von !== undefined);
  const nhan = (khoa: string) =>
    nhom === 'theo_tenant' ? nhanTenant(tenTenant, khoa) : nhanKhoa ? nhanKhoa(nhom, khoa) : khoa;

  return (
    <Tile className={KHOI_BANG}>
      <TileHeader
        icon={<BarChartOutlined />}
        tone="violet"
        title={t('nktvSuDungAi.phanRa.tieuDe')}
        mo={t('nktvSuDungAi.phanRa.moTa')}
      />
      <div className="overflow-x-auto border-b border-default px-5 py-3">
        <Segmented<NhomPhanRa>
          size="small"
          aria-label={t('nktvSuDungAi.phanRa.nhom')}
          value={nhom}
          onChange={setNhom}
          options={cacNhom.map((k) => ({ value: k, label: t(`nktvSuDungAi.phanRa.${k}`) }))}
        />
      </div>
      <DataTable<NhomAi>
        caption={t('nktvSuDungAi.phanRa.bang')}
        className={LE_BANG}
        rowKey="khoa"
        size="small"
        dataSource={dong}
        pagination={false}
        locale={{
          emptyText: (
            <Empty
              icon={InboxOutlined}
              title={t('nktvSuDungAi.phanRa.rongTieuDe')}
              description={t('nktvSuDungAi.phanRa.rongMoTa')}
            />
          ),
        }}
        columns={[
          {
            title: t('nktvSuDungAi.phanRa.cotKhoa'),
            render: (_v, r) => (
              <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                <span className="max-w-[20rem] truncate text-sm text-content-primary" title={r.khoa}>
                  {nhan(r.khoa)}
                </span>
                {coGiaVon && (r.so_chua_co_gia ?? 0) > 0 ? (
                  <span title={t('nktvSuDungAi.phanRa.chuaCoGiaMoTa')} className="inline-flex">
                    <Badge variant="danger">
                      {t('nktvSuDungAi.phanRa.chuaCoGia', { so: soNguyen(r.so_chua_co_gia, locale) })}
                    </Badge>
                  </span>
                ) : null}
                {/* Chỉ ở cách nhóm theo mô hình: ở đó `khoa` chính là tên mô hình cần khai giá vốn. */}
                {coGiaVon && (r.so_chua_co_gia ?? 0) > 0 && nhom === 'theo_mo_hinh' ? (
                  <LienKetKhaiGia moHinh={r.khoa} />
                ) : null}
                {nenTang && (r.so_co_canh_bao ?? 0) > 0 ? (
                  <span title={t('nktvSuDungAi.phanRa.coCanhBaoMoTa')} className="inline-flex">
                    <Badge variant="warning">
                      {t('nktvSuDungAi.phanRa.coCanhBao', { so: soNguyen(r.so_co_canh_bao, locale) })}
                    </Badge>
                  </span>
                ) : null}
              </span>
            ),
          },
          {
            title: t('nktvSuDungAi.phanRa.cotSoLuot'),
            width: 100,
            align: 'right',
            render: (_v, r) => <span className="tabular-nums">{soNguyen(r.so_luot, locale)}</span>,
          },
          cotFox<NhomAi>(t, locale),
          ...(nenTang ? [cotTokenThat<NhomAi>(t, locale)] : []),
          ...(coGiaVon
            ? [
                {
                  title: t('nktvSuDungAi.cotGiaVon'),
                  width: 150,
                  align: 'right' as const,
                  render: (_v: unknown, r: NhomAi) => (
                    <span className="tabular-nums text-content-secondary">
                      {soTien(r.chi_phi_von ?? null, r.don_vi_tien ?? null, locale) ?? TRONG}
                    </span>
                  ),
                },
              ]
            : []),
          {
            title: t('nktvSuDungAi.phanRa.cotDoTre'),
            width: 120,
            align: 'right',
            render: (_v, r) =>
              r.do_tre_tb_ms === null ? (
                <span className="tabular-nums text-content-muted" title={t('nktvSuDungAi.phanRa.doTreTrong')}>
                  {TRONG}
                </span>
              ) : (
                <span className="tabular-nums">{`${soNguyen(r.do_tre_tb_ms, locale)} ms`}</span>
              ),
          },
          {
            title: t('nktvSuDungAi.phanRa.cotTyLeLoi'),
            width: 120,
            align: 'right',
            render: (_v, r) => (
              <span
                className={
                  r.so_that_bai > 0 ? 'font-medium tabular-nums text-danger-700' : 'tabular-nums text-content-secondary'
                }
                title={t('nktvSuDungAi.phanRa.tyLeLoiMoTa', {
                  loi: soNguyen(r.so_that_bai, locale),
                  tong: soNguyen(r.so_luot, locale),
                })}
              >
                {tyLe(r.so_that_bai, r.so_luot, locale)}
              </span>
            ),
          },
        ]}
      />
    </Tile>
  );
}
